/**
 * Where this person belongs after signing in.
 *
 * The server decides, from ratified capability, using the same evaluator the
 * workspaces themselves use. The client asks; it never works it out from a
 * role name.
 */
import { api, apiRequest } from './api';

export type WorkspaceKey =
  | 'PATIENT_ADVOCATE' | 'UMED' | 'MANIFEST' | 'FINANCE' | 'SCHEDULING' | 'BUILDINGS' | 'PROVIDER' | 'DIALER' | 'RECORD_FLOW' | 'REPORTING' | 'ADMIN';

export interface Workspace {
  key: WorkspaceKey;
  label: string;
  description: string;
  path: string;
  /** Both gates pass: the ratified capability, and the eMerge operator allowlist. */
  available: boolean;
  /** Which gate is missing, when one is. */
  blocked_by: 'PHI_OPERATOR_ALLOWLIST' | null;
  /**
   * Whether it can be the landing page (the server's list of keys the preference
   * table stores). False for Record Flow and Reporting: offered, never stored.
   */
  preferable?: boolean;
}

export type ResolvedBy = 'PREFERENCE' | 'LAST_USED' | 'ADMIN_DEFAULT' | 'PRIORITY' | 'NO_WORKSPACE';

export interface WorkspaceIndex {
  /** Where to send them. `null` means the person holds no workspace at all. */
  home: string | null;
  resolved_by?: ResolvedBy;
  /** The default the person chose, when it is still one they hold. */
  preferred?: WorkspaceKey | null;
  admin: boolean;
  phi_operator: boolean;
  workspaces: Workspace[];
}

interface Result<T> { success: boolean; data?: T; error?: string; code?: string; status?: number; message?: string }

export const workspacesApi = {
  index: async (): Promise<Result<WorkspaceIndex>> =>
    (await api.get('/emerge/workspaces')) as Result<WorkspaceIndex>,
  /** Remember the workspace just opened. Fire-and-forget: it is navigation memory only. */
  visit: (key: WorkspaceKey) =>
    apiRequest<{ last: WorkspaceKey }>('POST', '/emerge/workspaces/visit', { key }),
  /** Choose where to land after signing in, or clear the choice with `null`. */
  setPreference: (key: WorkspaceKey | null) =>
    apiRequest<{ preferred: WorkspaceKey | null }>('PUT', '/emerge/workspaces/preference', { key }),
};

/**
 * The destination for a person who has just signed in, or `null` to keep
 * whatever the caller would have done.
 *
 * Never throws and never blocks the sign-in: if this call fails the person is
 * still logged in, and landing somewhere imperfect beats being held at the
 * door by a lookup that is only about navigation.
 */
export async function homeForUser(): Promise<string | null> {
  try {
    const res = await workspacesApi.index();
    return res.success && res.data ? res.data.home : null;
  } catch {
    return null;
  }
}

/** Record that a workspace was opened; never throws. */
export function rememberWorkspace(key: WorkspaceKey): void {
  Promise.resolve()
    .then(() => workspacesApi.visit(key))
    .catch(() => undefined);
}
